import { useEffect, useState } from 'react';
import { T, Var } from 'gt-react';
import { CaretDown, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

export interface ApiCallApprovalRequest {
  toolCallId: string;
  endpointId: string;
  args: Record<string, unknown>;
  reason: string;
  expiresAt: number;
}

type ApprovalResponse =
  | { status: 'executed'; resultSummary?: unknown }
  | { status: 'failed'; error?: string };

type RejectResponse = { status: 'rejected' };

export interface ApiCallApprovalCardProps {
  request: ApiCallApprovalRequest;
  serverStatus?: 'approved' | 'rejected' | 'timed_out';
  onApprove: (pendingCallId: string) => Promise<ApprovalResponse>;
  onReject: (pendingCallId: string) => Promise<RejectResponse>;
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function ApiCallApprovalCard({
  request,
  serverStatus,
  onApprove,
  onReject,
}: ApiCallApprovalCardProps) {
  const [decision, setDecision] = useState<
    ApprovalResponse | RejectResponse | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    const delay = request.expiresAt - Date.now();
    if (delay <= 0) return;
    const timeout = window.setTimeout(() => {
      setClock(request.expiresAt);
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [request.expiresAt]);

  const terminalStatus =
    decision?.status ??
    serverStatus ??
    (clock >= request.expiresAt ? 'timed_out' : undefined);
  const isDisabled = isSubmitting || terminalStatus !== undefined;

  async function submitDecision(
    action: () => Promise<ApprovalResponse | RejectResponse>,
  ) {
    setIsSubmitting(true);
    setRequestError(null);
    try {
      setDecision(await action());
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : String(error));
    }
    setIsSubmitting(false);
  }

  async function handleApprove() {
    await submitDecision(() => onApprove(request.toolCallId));
  }

  async function handleReject() {
    await submitDecision(() => onReject(request.toolCallId));
  }

  return (
    <Card className="border-amber-500/40 bg-amber-500/5">
      <CardHeader className="flex-row items-start gap-2 p-3 pb-2">
        <ShieldCheck
          size={18}
          weight="duotone"
          className="mt-0.5 shrink-0 text-amber-600"
        />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{request.reason}</p>
          <code className="block truncate text-xs text-muted-foreground">
            {request.endpointId}
          </code>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-3 pt-0">
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-1 text-xs"
            >
              <T context="button that expands arguments for an AI-requested internal operation">
                Show arguments
              </T>
              <CaretDown size={12} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-[11px] whitespace-pre-wrap">
              {formatJson(request.args)}
            </pre>
          </CollapsibleContent>
        </Collapsible>

        {terminalStatus === 'executed' && (
          <div className="rounded bg-muted p-2 text-xs">
            <p className="font-medium">
              <T context="status shown after an approved AI operation successfully executes">
                Executed
              </T>
            </p>
            {'resultSummary' in (decision ?? {}) &&
              decision?.status === 'executed' && (
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap">
                  {formatJson(decision.resultSummary)}
                </pre>
              )}
          </div>
        )}
        {terminalStatus === 'failed' && (
          <p className="text-xs text-destructive">
            <T context="status shown after an approved AI operation fails">
              Failed:{' '}
              <Var>{decision?.status === 'failed' && decision.error}</Var>
            </T>
          </p>
        )}
        {terminalStatus === 'approved' && (
          <p className="text-xs text-muted-foreground">
            <T context="status shown when an AI-requested operation was approved and its detailed result is no longer available">
              Approved
            </T>
          </p>
        )}
        {terminalStatus === 'rejected' && (
          <p className="text-xs text-muted-foreground">
            <T context="status shown after the user rejects an AI-requested operation">
              Rejected
            </T>
          </p>
        )}
        {terminalStatus === 'timed_out' && (
          <p className="text-xs text-muted-foreground">
            <T context="status shown when an AI operation approval request expires">
              Timed out
            </T>
          </p>
        )}
        {requestError && (
          <p className="text-xs text-destructive">
            <Var>{requestError}</Var>
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDisabled}
            onClick={handleReject}
          >
            <T context="button to reject an AI-requested internal operation">
              Reject
            </T>
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isDisabled}
            onClick={handleApprove}
          >
            <T context="button to approve and execute an AI-requested internal operation">
              Approve
            </T>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
